import { httpClient } from '@/shared/api/httpClient'
import { isPortalUuid, normalizePortalAmount } from '../services/portalSafety'
import { parsePortalFlowQuote, validatePortalFlowTypes } from '../services/portalFlowQuote'
import {
  PORTAL_STATUSES,
  PORTAL_FLOW_RESULT_STATUSES,
  PORTAL_FLOW_STATUSES,
  type PortalFlowItem,
  type PortalFlowPage,
  type PortalFlowQuote,
  type PortalFlowCreateRequest,
  type PortalQuote,
  type PortalStatus,
  type PortalTopupRequest,
  type PortalTopupResponse,
} from '../types/portal'

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('PORTAL_INVALID_RESPONSE')
  return value as Record<string, unknown>
}

function data(response: unknown): Record<string, unknown> {
  const envelope = record(response)
  if (envelope.result !== undefined && envelope.result !== 'success')
    throw new Error('PORTAL_INVALID_RESPONSE')
  return record(envelope.data)
}

function decimal(value: unknown, scale: number): boolean {
  return (
    typeof value === 'string' &&
    new RegExp(`^\\d{1,30}(?:\\.\\d{1,${scale}})?$`).test(value)
  )
}

function nullableDecimal(value: unknown, scale: number): boolean {
  return value === null || decimal(value, scale)
}

function identity(value: Record<string, unknown>): boolean {
  return isPortalUuid(value.attempt_id) && isPortalUuid(value.invoice_uuid)
}

function boundedSignal(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(30000)
  return signal ? AbortSignal.any([signal, timeout]) : timeout
}

function validateStatus(
  value: Record<string, unknown>,
  statuses: readonly string[],
): void {
  if (
    !identity(value) ||
    !statuses.some((status) => status === value.status) ||
    !decimal(value.invoice_amount_usdt, 6) ||
    !nullableDecimal(value.payment_amount_rub, 2) ||
    !nullableDecimal(value.credited_usdt, 6) ||
    (value.status === 'completed' && !decimal(value.credited_usdt, 6)) ||
    !(value.pay_url === null || typeof value.pay_url === 'string') ||
    !(value.error_code === null || typeof value.error_code === 'string')
  ) {
    throw new Error('PORTAL_INVALID_RESPONSE')
  }
}

/** New outcome rows are numerically sorted; the original selection need not be. */
function validatePurchaseResult(item: Record<string, unknown>, types: string[]): void {
  if (typeof item.purchase_complete !== 'boolean' ||
      !decimal(item.actual_gnk_amount, 9) || !/[1-9]/.test(String(item.actual_gnk_amount)) ||
      !types.every(type => /^gonka-game_f[1-9][0-9]*$/.test(type)) ||
      !Array.isArray(item.flows) || item.flows.length !== types.length) {
    throw new Error('PORTAL_INVALID_RESPONSE')
  }
  // Fixed prefix and no leading zeroes: length + lexical comparison is exact
  // numeric ordering without narrowing potentially large level IDs to Number.
  const ordered = [...types].sort((a, b) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0))
  let bought = 0
  let stopped = false
  const statuses: unknown[] = []
  item.flows.forEach((value, index) => {
    const flow = record(value)
    if (flow.type !== ordered[index] ||
        (flow.price_gnk !== null && !decimal(flow.price_gnk, 9))) {
      throw new Error('PORTAL_INVALID_RESPONSE')
    }
    if (flow.purchase_status === 'purchased') {
      if (stopped || flow.price_gnk === null || !/[1-9]/.test(String(flow.price_gnk)) ||
          !['prepaid', 'activating', 'activated'].some(status => status === flow.status)) {
        throw new Error('PORTAL_INVALID_RESPONSE')
      }
      bought++
    } else {
      if (!['insufficient', 'not_attempted'].some(status => status === flow.purchase_status) ||
          flow.status !== flow.purchase_status ||
          (stopped && flow.purchase_status !== 'not_attempted') ||
          (flow.purchase_status === 'insufficient' &&
            (flow.price_gnk === null || !/[1-9]/.test(String(flow.price_gnk))))) {
        throw new Error('PORTAL_INVALID_RESPONSE')
      }
      stopped = true
    }
    statuses.push(flow.status)
  })
  const complete = bought === types.length
  const expectedStatus = bought === 0 ? 'exchanged' : !complete ? 'partially_purchased'
    : statuses.every(status => status === 'prepaid') ? 'prepaid'
      : statuses.every(status => status === 'activated') ? 'activated' : 'activating'
  if (item.purchase_complete !== complete || item.status !== expectedStatus) {
    throw new Error('PORTAL_INVALID_RESPONSE')
  }
}

function parseFlow(value: unknown): PortalFlowItem {
  const item = record(value)
  validateStatus(item, PORTAL_FLOW_STATUSES)
  const zeroAllocation = item.status === 'exchanged' && item.progress_reason === 'wallet_zero_allocation'
  const zeroActual = typeof item.actual_gnk_amount === 'string' && decimal(item.actual_gnk_amount, 9) && !/[1-9]/.test(item.actual_gnk_amount)
  if (
    !Array.isArray(item.types) ||
    item.types.length === 0 ||
    !item.types.every((type) => typeof type === 'string' && type.length > 0) ||
    new Set(item.types).size !== item.types.length ||
    !decimal(item.gnk_amount, 9) ||
    (item.actual_gnk_amount !== undefined &&
      (!decimal(item.actual_gnk_amount, 9) || (zeroActual && !zeroAllocation))) ||
    (item.progress_reason === 'wallet_zero_allocation' && (!zeroAllocation || !zeroActual)) ||
    !isPortalUuid(item.client_request_id) ||
    typeof item.created_at !== 'number' ||
    !Number.isSafeInteger(item.created_at) ||
    item.created_at < 0 ||
    !(
      item.progress_reason === undefined ||
      item.progress_reason === null ||
      typeof item.progress_reason === 'string'
    )
  ) {
    throw new Error('PORTAL_INVALID_RESPONSE')
  }
  const types = item.types as string[]
  const hasPurchaseResult = item.purchase_complete !== undefined ||
    ['partially_purchased', 'activating'].includes(String(item.status)) ||
    (Array.isArray(item.flows) && item.flows.some(value => {
      const flow = record(value)
      return flow.purchase_status !== undefined || flow.price_gnk !== undefined
    }))
  if (hasPurchaseResult) {
    validatePurchaseResult(item, types)
    return item as unknown as PortalFlowItem
  }
  // Exchanged is terminal without a purchase: actual credit, or an explicitly
  // confirmed zero allocation. A purchase result would contradict either case.
  if (item.status === 'exchanged' &&
      (item.actual_gnk_amount === undefined || item.flows !== undefined)) {
    throw new Error('PORTAL_INVALID_RESPONSE')
  }
  if (item.flows !== undefined) {
    if (!Array.isArray(item.flows) || item.flows.length !== types.length)
      throw new Error('PORTAL_INVALID_RESPONSE')
    item.flows.forEach((value, index) => {
      const flow = record(value)
      if (
        flow.type !== types[index] ||
        !PORTAL_FLOW_RESULT_STATUSES.some((status) => status === flow.status)
      )
        throw new Error('PORTAL_INVALID_RESPONSE')
    })
  }
  if (item.status === 'prepaid' || item.status === 'activated') {
    if (
      !Array.isArray(item.flows) ||
      !item.flows.every((value) => record(value).status === item.status)
    )
      throw new Error('PORTAL_INVALID_RESPONSE')
  }
  return item as unknown as PortalFlowItem
}

// ownedStatus adds these fields only for a server-validated basket contract.
// Reject incomplete/mixed baskets rather than falling back to a top-up.
const flowFields = ['types', 'gnk_amount', 'client_request_id', 'created_at',
  'progress_reason', 'actual_gnk_amount', 'flows', 'purchase_complete']

export type PortalReturnStatus =
  | { kind: 'topup'; item: PortalStatus }
  | { kind: 'flow'; item: PortalFlowItem }

function parseTopup(value: Record<string, unknown>): PortalStatus {
  if (flowFields.some(key => key in value)) throw new Error('PORTAL_INVALID_RESPONSE')
  validateStatus(value, PORTAL_STATUSES)
  const hasInvoiceTerms = ['invoice_currency', 'invoice_amount', 'credited_amount'].some(key => key in value)
  if (hasInvoiceTerms) {
    const currency = value.invoice_currency
    const scale = currency === 'GNK' ? 9 : currency === 'USDT' ? 6 : 8
    if (typeof currency !== 'string' || !/^(?:[A-Z][A-Z0-9]{1,11}|(?:erc20|bep20|trc20)\|USDT)$/.test(currency) ||
        !decimal(value.invoice_amount, scale) ||
        !nullableDecimal(value.credited_amount, scale) ||
        (value.status === 'completed' && !decimal(value.credited_amount, scale))) {
      throw new Error('PORTAL_INVALID_RESPONSE')
    }
  }
  return value as unknown as PortalStatus
}

async function statusRecord(
  attemptId: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  if (!isPortalUuid(attemptId)) throw new Error('PORTAL_INVALID_ATTEMPT')
  const value = data(
    await httpClient.get<unknown>(
      `/payment/portal/status?attempt_id=${encodeURIComponent(attemptId)}`,
      { signal: boundedSignal(signal), retry: 0 },
    ),
  )
  if (value.attempt_id !== attemptId) throw new Error('PORTAL_INVALID_RESPONSE')
  return value
}

export const PortalApi = {
  async flowQuote(types: string[], signal?: AbortSignal): Promise<PortalFlowQuote> {
    validatePortalFlowTypes(types)
    const response = record(await httpClient.post<unknown>(
      '/payment/portal/flow/quote', { types: [...types] },
      { signal: boundedSignal(signal), retry: 0 },
    ))
    if (response.result !== 'success') throw new Error('PORTAL_INVALID_RESPONSE')
    return parsePortalFlowQuote(data(response), types)
  },

  async flowCreate(request: PortalFlowCreateRequest, signal?: AbortSignal): Promise<PortalFlowItem> {
    if (!isPortalUuid(request.quote_id) || !isPortalUuid(request.client_request_id))
      throw new Error('PORTAL_INVALID_REQUEST')
    const response = record(await httpClient.post<unknown>(
      '/payment/portal/flow/create',
      { quote_id: request.quote_id, client_request_id: request.client_request_id },
      { signal: boundedSignal(signal), retry: 0 },
    ))
    if (response.result !== 'success') throw new Error('PORTAL_INVALID_RESPONSE')
    const item = parseFlow(data(response))
    if (item.client_request_id !== request.client_request_id)
      throw new Error('PORTAL_INVALID_RESPONSE')
    return item
  },

  async quote(amount?: string, signal?: AbortSignal): Promise<PortalQuote> {
    if (amount !== undefined && !normalizePortalAmount(amount))
      throw new Error('PORTAL_INVALID_AMOUNT')
    const value = data(
      await httpClient.post<unknown>(
        '/payment/portal/quote',
        amount === undefined ? {} : { amount_usdt: amount },
        { signal: boundedSignal(signal), retry: 0 },
      ),
    )
    if (
      typeof value.available !== 'boolean' ||
      typeof value.kyc_required !== 'boolean' ||
      !(value.reason === null || typeof value.reason === 'string') ||
      !(
        value.invoice_amount_usdt === '' ||
        decimal(value.invoice_amount_usdt, 6)
      ) ||
      !nullableDecimal(value.payment_amount_rub, 2) ||
      !nullableDecimal(value.min_rub, 2) ||
      !nullableDecimal(value.max_rub, 2) ||
      (amount === undefined &&
        value.available &&
        (!decimal(value.rate_rub, 18) ||
          !/[1-9]/.test(String(value.rate_rub)) ||
          typeof value.min_usdt !== 'string' ||
          !normalizePortalAmount(value.min_usdt))) ||
      (amount !== undefined &&
        value.available &&
        (!decimal(value.invoice_amount_usdt, 6) ||
          !decimal(value.payment_amount_rub, 2)))
    ) {
      throw new Error('PORTAL_INVALID_RESPONSE')
    }
    return value as unknown as PortalQuote
  },

  async topup(
    request: PortalTopupRequest,
    signal?: AbortSignal,
  ): Promise<PortalTopupResponse> {
    if (
      !isPortalUuid(request.client_request_id) ||
      !normalizePortalAmount(request.amount_usdt)
    )
      throw new Error('PORTAL_INVALID_REQUEST')
    const value = data(
      await httpClient.post<unknown>('/payment/portal/topup', request, {
        signal: boundedSignal(signal),
        retry: 0,
      }),
    )
    if (
      !identity(value) ||
      (value.result !== 'setup_pending' && value.result !== 'redirect') ||
      (value.result === 'redirect' &&
        (typeof value.pay_url !== 'string' ||
          !decimal(value.invoice_amount_usdt, 6) ||
          !decimal(value.payment_amount_rub, 2)))
    ) {
      throw new Error('PORTAL_INVALID_RESPONSE')
    }
    // Navigation validation is separate so an unsafe URL cannot erase the attempt id.
    return value as unknown as PortalTopupResponse
  },

  async status(attemptId: string, signal?: AbortSignal): Promise<PortalStatus> {
    const value = await statusRecord(attemptId, signal)
    return parseTopup(value)
  },

  async returnStatus(attemptId: string, signal?: AbortSignal): Promise<PortalReturnStatus> {
    const value = await statusRecord(attemptId, signal)
    return flowFields.some(key => key in value)
      ? { kind: 'flow', item: parseFlow(value) }
      : { kind: 'topup', item: parseTopup(value) }
  },

  async flowList(
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PortalFlowPage> {
    if (cursor !== undefined && !isPortalUuid(cursor))
      throw new Error('PORTAL_INVALID_CURSOR')
    const response = record(
      await httpClient.get<unknown>(
        `/payment/portal/flow/list${cursor === undefined ? '' : `?cursor=${encodeURIComponent(cursor)}`}`,
        { signal: boundedSignal(signal), retry: 0 },
      ),
    )
    const value = data(response)
    if (
      response.result !== 'success' ||
      !Array.isArray(value.items) ||
      !(value.next_cursor === null || isPortalUuid(value.next_cursor))
    ) {
      throw new Error('PORTAL_INVALID_RESPONSE')
    }
    return { items: value.items.map(parseFlow), next_cursor: value.next_cursor,
      flow_available: value.flow_available === true }
  },

  async flowStatus(
    attemptId: string,
    signal?: AbortSignal,
  ): Promise<PortalFlowItem> {
    return parseFlow(await statusRecord(attemptId, signal))
  },
}
